import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import { Button, List, ListItem, Typography } from '@mui/material';

import { ApiProvider } from '@/provider/ApiProvider';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import Box from '@mui/system/Box';
import StyledLink from '@/components/styled/StyledLink';
import Tooltip from '@mui/material/Tooltip';
import { useUserData } from '@/hooks/useUserData';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';

export default function SidebarSettings() {
  const [settingsStyle, setSettingsStyle] = React.useState('full');
  const { showSettingsSidebar, activeSettingsLink, updateValue } = useSidebarNavigation();
  const { activeSection, activeName, activeUrl } = activeSettingsLink;

  const { globalFilters } = useGlobalFilters();
  const brand_id = globalFilters.headerBrandId;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userData: { type: userType },
  } = useUserData();
  const isVisible =
    !location.pathname.includes('workinprogress') && (showSettingsSidebar || !!activeSection);

  const setupSections = [
    {
      sectionTitle: 'DBS',
      links: [
        ...(Boolean(ApiProvider.permission('excel_uploads'))
          ? [{ to: '/dbs', title: 'Excel uploads' }]
          : []),
        ...(Boolean(ApiProvider.permission('data_crawlers_list'))
          ? [{ to: '/workinprogress', title: 'Data Crawlers' }]
          : []),
        ...(Boolean(ApiProvider.permission('duplicate_checks'))
          ? [{ to: '/workinprogress', title: 'Duplicate Checks' }]
          : []),
        ...(Boolean(ApiProvider.permission('currency_exchange_rate_list'))
          ? [{ to: '/currencyexchange', title: 'Currency Exchange Rates' }]
          : []),
        { to: '/audienceinterest', title: 'Audience Interest' },
      ],
    },
    {
      sectionTitle: 'Manage Users',
      links: [
        ...(Boolean(ApiProvider.permission('role_list'))
          ? [{ to: '/userrolelist', title: 'Roles' }]
          : []),
        ...(Boolean(ApiProvider.permission('user_list'))
          ? [{ to: '/userlist', title: 'All Users' }]
          : []),
      ],
    },
    {
      sectionTitle: 'Your organization',
      links: [
        // TODO: If user is of property type-show Edit Theme and hide all other links.
        ...([2, 3, 4].includes(userType) && Boolean(ApiProvider.permission('view_theme_screen'))
          ? [
              {
                to: '/theme/edit',
                title: 'Edit Theme',
              },
            ]
          : []),
        ...([2, 3, 4].includes(userType) && Boolean(ApiProvider.permission('view_email_template'))
          ? [
              {
                to: '/emailtemplates',
                title: 'Email Templates',
              },
            ]
          : []),
        { to: '/loginpreferences', title: 'Login Preferences' },
        ...([1, 2, 4].includes(userType) && Boolean(ApiProvider.permission('bonus_manage'))
          ? [
              {
                to: 'javascript:void(0);',
                title: 'Bonus management',
                onClick: () => goToPage('Bonus management'),
                brandDependent: true,
              },
            ]
          : []),

        ...([1, 2, 4].includes(userType) && Boolean(ApiProvider.permission('bonus_manage'))
          ? [
              {
                title: 'Objectives',
                onClick: () => goToPage('Objectives'),
                brandDependent: true,
              },
            ]
          : []),
      ],
    },
  ];

  useEffect(() => {
    let parentSection = false;
    let currentLink = false;

    for (const section of setupSections) {
      if (section.links.some((link) => link.to === location.pathname)) {
        parentSection = section.sectionTitle;
        currentLink = section.links.find((link) => link.to === location.pathname);
        break;
      }
    }

    updateValue(
      'activeSettingsLink',
      parentSection && currentLink
        ? {
            activeSection: parentSection,
            activeName: currentLink.title,
            activeUrl: currentLink.to,
          }
        : {
            activeSection: false,
            activeName: '',
            activeUrl: '',
          },
    );
  }, [location.pathname]);

  useEffect(() => {
    // TODO Refactor to ternary if nothing else will be added.
    if (activeSettingsLink.activeSection) {
      setSettingsStyle('mini');
    } else {
      setSettingsStyle('full');
    }
  }, [activeSettingsLink.activeSection]);

  const goToPage = (pageName) => {
    if (!brand_id || brand_id === '') {
      updateValue('showSettingsSidebar', false);

      return;
    }

    switch (pageName) {
      case 'Objectives':
        updateValue('showSettingsSidebar', false);
        localStorage.setItem('displaytab', 'Objectives');
        navigate('/brandprofile/' + brand_id);
        break;
      case 'Bonus management':
        updateValue('showSettingsSidebar', false);
        navigate('/brandbonus');
        break;
      default:
        break;
    }
  };

  const sectionHeaderStyles = {
    color: 'typography.main',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'uppercase',
    marginBottom: '8px',
  };

  return (
    <Box
      sx={{
        visibility: isVisible ? 'visible' : 'hidden',
        position: 'fixed',
        bottom: '0',
        top: '50px',
        left: !!activeSection ? '65px' : showSettingsSidebar ? '65px' : '-280px',
        transition: 'left .3s',
        zIndex: '1199',
        display: 'flex',
      }}
    >
      <Box
        className='content'
        sx={{
          p: settingsStyle === 'full' ? '36px 30px' : '16px',
          width: !!activeSection ? '210px' : '275px',
          boxSizing: 'border-box',
          backgroundColor: 'primary.main',
          height: '100%',
          position: 'relative',
          overflow: 'scroll',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {!!activeSection ? null : (
          <Box
            className='close_commentbox'
            onClick={() => updateValue('showSettingsSidebar', false)}
          >
            <CloseIcon sx={{ color: 'primary.contrastText' }} />
          </Box>
        )}
        <List>
          {setupSections.map(({ sectionTitle, links }, index) => (
            <Box key={sectionTitle + index} sx={{ '&:not(:last-child)': { mb: '30px' } }}>
              {!!links.length && (
                <ListItem sx={{ padding: '0' }}>
                  <Box as='section'>
                    {activeSection ? (
                      <>
                        {sectionTitle === activeSection ? (
                          <Typography as='h4' sx={sectionHeaderStyles}>
                            {sectionTitle}
                          </Typography>
                        ) : (
                          <>
                            {links[0].to && links[0].to !== 'javascript:void(0);' ? (
                              <StyledLink
                                to={links[0].to}
                                onClick={() => updateValue('showSettingsSidebar', false)}
                              >
                                <Typography as='h4' sx={sectionHeaderStyles}>
                                  {sectionTitle}
                                </Typography>
                              </StyledLink>
                            ) : (
                              <Tooltip title='Please select a brand first' placement='top' arrow>
                                <Typography
                                  as='h4'
                                  sx={{ cursor: 'not-allowed', ...sectionHeaderStyles }}
                                >
                                  {sectionTitle}
                                </Typography>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Typography as='h4' sx={sectionHeaderStyles}>
                        {sectionTitle}
                      </Typography>
                    )}
                    {links && (
                      <Box
                        sx={{
                          flexDirection: 'column',
                          gap: '8px',
                          display: activeSection
                            ? sectionTitle === activeSection
                              ? 'flex'
                              : 'none'
                            : 'flex',
                        }}
                      >
                        {links.map(({ title, to, onClick, brandDependent }, index) => {
                          const isActive = title === activeName && to === activeUrl;

                          const typography = (
                            <Typography
                              sx={{
                                color: 'typography.main',
                                fontSize: '12px',
                                fontWeight: '400',
                                borderRadius: '40px',
                                textDecoration: isActive ? 'underline' : 'none',
                              }}
                            >
                              {title}
                            </Typography>
                          );

                          // Determine if the Tooltip should be used
                          const content =
                            brandDependent && !brand_id ? (
                              <Tooltip title='Please select a brand' placement='top' arrow>
                                {typography}
                              </Tooltip>
                            ) : (
                              typography
                            );

                          return to && to !== 'javascript:void(0);' ? (
                            <StyledLink
                              key={title + index}
                              disabled={!brand_id || false}
                              to={to}
                              onClick={() => updateValue('showSettingsSidebar', false)}
                            >
                              {content}
                            </StyledLink>
                          ) : (
                            <Button
                              key={title + index}
                              disabled={!brand_id || false}
                              onClick={onClick}
                              variant='text'
                              sx={{
                                padding: '0',
                                justifyContent: 'flex-start',
                                color: `typography.main`,
                                '&.Mui-disabled': {
                                  color: `typography.main`,
                                  opacity: '.6',
                                },
                              }}
                            >
                              {content}
                            </Button>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </ListItem>
              )}
            </Box>
          ))}
        </List>
      </Box>
      {!activeSection && (
        <Box
          sx={{
            transition: 'max-width .3s, opacity .3s',
            width: '100vw',
            maxWidth: showSettingsSidebar ? '9999px' : '0',
            opacity: showSettingsSidebar ? '1' : '0',
            overflow: 'hidden',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
          onClick={() => updateValue('showSettingsSidebar', false)}
        />
      )}
    </Box>
  );
}
